import {cn} from '@containers/html-utils'
import {endOfDay, format, isAfter, isSameDay} from 'date-fns'
import {observer} from 'mobx-react-lite'
import React from 'react'
import {TaskWithColor} from '../../calendar-store'
import {
  getDeadline,
  getTaskHeight,
  minTaskHeight,
  proportionPixelsDivideOnHour
} from '../../utils'
import {useDraggableStore} from '../draggable/draggable-store'
import {TaskWithStartDate} from './task-with-start-date'
import {TaskWithoutStartDate} from './task-without-start-date'

interface TasksWithStartDateProps {
  task: TaskWithColor
  currentHour: number
  currentDate: Date
  start: Date
  end: Date
  withoutStart?: boolean
  currentTime: Date
  showAllDayWithStartDateTask: boolean
}

export const TasksMapping = observer(
  // eslint-disable-next-line max-lines-per-function
  ({
    task,
    currentHour,
    currentDate,
    currentTime,
    start,
    end,
    withoutStart = false,
    showAllDayWithStartDateTask = false
  }: TasksWithStartDateProps) => {
    const [draggableState] = useDraggableStore()

    const startDate = withoutStart
      ? task.roundedDeadline
      : task.roundedStartDate
    const deadline = getDeadline(task, draggableState)

    const drag = isSameDay(deadline, end)
    const isAllDay =
      !isSameDay(deadline, currentDate) && !isSameDay(startDate, currentDate)
    const pointerEventsNone =
      draggableState.draggedItem.id &&
      draggableState.draggedItem.id !== task.task.id

    if (format(startDate, 'yyyy-MM-dd HH') === format(start, 'yyyy-MM-dd HH')) {
      if (withoutStart) {
        const height =
          draggableState.draggedItem.id === task.task.id &&
          draggableState.draggedItem.changeAction === 'change-deadline'
            ? getTaskHeight(startDate, deadline) + minTaskHeight
            : minTaskHeight

        const top =
          task.roundedDeadline.getMinutes() * proportionPixelsDivideOnHour - 15

        return (
          <TaskWithoutStartDate
            task={task}
            key={task.task.id}
            drag={drag}
            top={top}
            height={height < minTaskHeight ? minTaskHeight : height}
            currentTime={currentTime}
            changeDeadlineExpansion={true}
            pointerEventsNone={pointerEventsNone}
          />
        )
      } else {
        const height = getTaskHeight(startDate, deadline)
        const minutes = startDate.getMinutes()
        const top = minutes * proportionPixelsDivideOnHour
        const allDay = !isSameDay(deadline, start)
        return (
          <TaskWithStartDate
            task={task}
            key={task.task.id}
            drag={drag}
            top={top}
            height={
              allDay
                ? height + 20
                : height < minTaskHeight
                ? minTaskHeight
                : height
            }
            hideDeadline={!drag}
            className={cn(allDay && 'border-b-0 rounded-b-none')}
            changeDeadlineExpansion={height < 59}
            pointerEventsNone={pointerEventsNone}
            allDay={false}
          />
        )
      }
    }

    if (
      isSameDay(deadline, start) &&
      !isSameDay(startDate, end) &&
      startDate < end &&
      deadline > start
    ) {
      if (
        !isAllDay &&
        currentHour === 0 &&
        isAfter(currentDate, endOfDay(startDate))
      ) {
        const height = getTaskHeight(startDate, deadline, {
          startFromZero: true
        })
        const top = 0
        return (
          <TaskWithStartDate
            task={task}
            key={task.task.id}
            drag={drag}
            top={top}
            height={height - 1}
            showBottomDeadline
            customTop={-25}
            textOpacity={'opacity-60'}
            className={'border-t-0 rounded-t-none'}
            timeWithDate
            pointerEventsNone={pointerEventsNone}
            allDay={false}
          />
        )
      }
    }

    if (
      !isSameDay(deadline, start) &&
      !isSameDay(startDate, end) &&
      startDate < end &&
      deadline > start
    ) {
      if (
        isAllDay &&
        currentHour === 0 &&
        draggableState.draggedItem.id === task.task.id &&
        showAllDayWithStartDateTask
      ) {
        const height = getTaskHeight(startDate, deadline, {
          allDay: true,
          startFromZero: true
        })
        const top = 0
        return (
          <TaskWithStartDate
            allDay={true}
            task={task}
            key={task.task.id}
            top={top}
            height={height}
            drag={drag}
            showBottomDeadline
            textOpacity={'opacity-60'}
            pointerEventsNone={pointerEventsNone}
          />
        )
      }
    }

    return null
  }
)
